import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  path?: string; // canonical path, e.g. "/foto/dron"
  type?: "website" | "article";
  /** Optional structured data (single object or array). Rendered as a single JSON-LD <script> per route and cleaned up on unmount. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional per-page keywords, in addition to sitewide ones. */
  keywords?: string;
}

const SITE_URL = "https://wencek.xyz";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const setMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** Replace all hreflang <link> tags with the supplied alternates. */
const setHreflangAlternates = (alternates: Array<{ hreflang: string; href: string }>) => {
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  for (const a of alternates) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", a.hreflang);
    el.setAttribute("href", a.href);
    document.head.appendChild(el);
  }
};

const PAGE_JSONLD_ID = "seo-jsonld-page";

const SEO = ({ title, description, image, path = "", type = "website", jsonLd, keywords }: SEOProps) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img = image || DEFAULT_IMAGE;

    document.title = title;

    setMeta('meta[name="description"]', "name", "description", description);
    setLink("canonical", url);

    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:image"]', "property", "og:image", img);

    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", img);

    if (keywords) {
      setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }

    // Per-page hreflang (PL/EN both point at this URL — single bilingual page).
    setHreflangAlternates([
      { hreflang: "pl", href: url },
      { hreflang: "en", href: url },
      { hreflang: "x-default", href: url },
    ]);

    // Inject route-scoped JSON-LD payload.
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.id = PAGE_JSONLD_ID;
      const payload = Array.isArray(jsonLd)
        ? { "@context": "https://schema.org", "@graph": jsonLd }
        : { "@context": "https://schema.org", ...jsonLd };
      scriptEl.text = JSON.stringify(payload);
      // Remove any stale instance from a previous route before appending.
      document.head.querySelectorAll(`#${PAGE_JSONLD_ID}`).forEach((el) => el.remove());
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [title, description, image, path, type, jsonLd, keywords]);

  return null;
};

export default SEO;